import { useEffect, useRef, useState } from "react";
import { useVehicleT } from "./i18n.js";
import { useVehicles, vehicleStore } from "./store.js";

/**
 * The instrument cluster of the cockpit camera: speedometer and rev counter (redline marked),
 * the gear, fuel and the indicator arrows. Drawn procedurally in SVG and updated from the
 * simulation every animation frame through refs (no React render per frame).
 */

const START = -225;
const SWEEP = 270;

function arc(r: number, a0: number, a1: number): string {
  const p = (a: number) => {
    const t = (a * Math.PI) / 180;
    return `${(60 + r * Math.cos(t)).toFixed(2)} ${(60 + r * Math.sin(t)).toFixed(2)}`;
  };
  const large = a1 - a0 > 180 ? 1 : 0;
  return `M ${p(a0)} A ${r} ${r} 0 ${large} 1 ${p(a1)}`;
}

function Gauge({
  max,
  step,
  red,
  label,
  needle,
  value,
}: {
  max: number;
  step: number;
  red?: number;
  label: string;
  needle: React.RefObject<SVGLineElement | null>;
  value: React.RefObject<SVGTextElement | null>;
}) {
  const ticks: number[] = [];
  for (let v = 0; v <= max + 1e-6; v += step) ticks.push(v);
  return (
    <svg viewBox="0 0 120 120" className="veh-gauge" aria-hidden="true">
      <circle cx="60" cy="60" r="57" className="veh-gauge-face" />
      <path d={arc(50, START, START + SWEEP)} className="veh-gauge-track" />
      {red !== undefined ? (
        <path d={arc(50, START + (SWEEP * red) / max, START + SWEEP)} className="veh-gauge-red" />
      ) : null}
      {ticks.map((v) => {
        const a = ((START + (SWEEP * v) / max) * Math.PI) / 180;
        return (
          <g key={v}>
            <line
              x1={60 + 44 * Math.cos(a)}
              y1={60 + 44 * Math.sin(a)}
              x2={60 + 50 * Math.cos(a)}
              y2={60 + 50 * Math.sin(a)}
              className="veh-gauge-tick"
            />
            <text x={60 + 35 * Math.cos(a)} y={62 + 35 * Math.sin(a)} className="veh-gauge-num">
              {red !== undefined ? v / 1000 : v}
            </text>
          </g>
        );
      })}
      <text x="60" y="88" className="veh-gauge-label">
        {label}
      </text>
      <text ref={value} x="60" y="104" className="veh-gauge-value">
        0
      </text>
      <line ref={needle} x1="60" y1="60" x2="60" y2="16" className="veh-gauge-needle" />
      <circle cx="60" cy="60" r="4" className="veh-gauge-hub" />
    </svg>
  );
}

export function Dashboard() {
  const t = useVehicleT();
  const id = useVehicles((s) => s.drivingId);
  const [mode, setMode] = useState(vehicleStore.camMode);
  const speedNeedle = useRef<SVGLineElement>(null);
  const speedValue = useRef<SVGTextElement>(null);
  const revNeedle = useRef<SVGLineElement>(null);
  const revValue = useRef<SVGTextElement>(null);
  const gear = useRef<HTMLSpanElement>(null);
  const fuel = useRef<HTMLSpanElement>(null);
  const left = useRef<HTMLSpanElement>(null);
  const right = useRef<HTMLSpanElement>(null);
  const sim = vehicleStore.sim;
  const v = id ? vehicleStore.vehicleOf(id) : undefined;
  const model = v ? vehicleStore.modelOf(v.modelId) : undefined;
  const top = Math.max(60, Math.ceil(((model?.topSpeedKmh ?? 160) * 1.1) / 20) * 20);
  const redline = sim?.tune.redline ?? 6500;
  const revMax = Math.ceil((sim?.tune.limiter ?? 7000) / 1000) * 1000;
  useEffect(() => {
    if (!id) return;
    let raf = 0;
    const tick = () => {
      raf = requestAnimationFrame(tick);
      if (vehicleStore.camMode !== mode) setMode(vehicleStore.camMode);
      const s = vehicleStore.sim;
      if (!s) return;
      const kmh = Math.abs(s.speed) * 3.6;
      const aS = START + 90 + (SWEEP * Math.min(kmh, top)) / top;
      speedNeedle.current?.setAttribute("transform", `rotate(${aS.toFixed(1)} 60 60)`);
      if (speedValue.current) speedValue.current.textContent = String(Math.round(kmh));
      const rpm = s.box.rpm;
      const aR = START + 90 + (SWEEP * Math.min(rpm, revMax)) / revMax;
      revNeedle.current?.setAttribute("transform", `rotate(${aR.toFixed(1)} 60 60)`);
      if (revValue.current) revValue.current.textContent = String(Math.round(rpm / 50) * 50);
      if (gear.current) {
        const g = s.box.gear;
        gear.current.textContent = g < 0 ? "R" : g === 0 ? "N" : String(g);
        gear.current.dataset["red"] = rpm > s.tune.redline ? "1" : "0";
      }
      if (fuel.current) {
        const f = Math.max(0.08, 1 - (s.distance % 60000) / 60000);
        fuel.current.style.width = `${(f * 100).toFixed(0)}%`;
      }
      const on = Math.floor(performance.now() / 380) % 2 === 0;
      const ind = vehicleStore.indicator;
      left.current?.classList.toggle("on", on && (ind === "left" || ind === "hazard"));
      right.current?.classList.toggle("on", on && (ind === "right" || ind === "hazard"));
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [id, mode, top, revMax]);
  if (!id || mode !== "cockpit") {
    return id ? <ModeWatch onChange={setMode} /> : null;
  }
  return (
    <div className="veh-dash" data-testid="drive-dashboard">
      <Gauge
        max={top}
        step={top > 160 ? 40 : 20}
        label={t("kmh")}
        needle={speedNeedle}
        value={speedValue}
      />
      <div className="veh-dash-mid">
        <span ref={left} className="veh-ind veh-ind-l">
          ◀
        </span>
        <span ref={gear} className="veh-dash-gear" data-testid="dash-gear">
          1
        </span>
        <span ref={right} className="veh-ind veh-ind-r">
          ▶
        </span>
        <div className="veh-fuel" aria-label={t("fuel")}>
          <small>{t("fuel")}</small>
          <span ref={fuel} />
        </div>
      </div>
      <Gauge
        max={revMax}
        step={1000}
        red={redline}
        label="rpm"
        needle={revNeedle}
        value={revValue}
      />
    </div>
  );
}

/** Re-renders the dashboard when the camera mode changes (key 6), without a per-frame render. */
function ModeWatch({ onChange }: { onChange: (m: typeof vehicleStore.camMode) => void }) {
  useEffect(() => {
    let last = vehicleStore.camMode;
    const id = setInterval(() => {
      if (vehicleStore.camMode !== last) {
        last = vehicleStore.camMode;
        onChange(last);
      }
    }, 150);
    return () => clearInterval(id);
  }, [onChange]);
  return null;
}
